import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { VirtualAdminTable, type VirtualColumnDef } from '@/components/ui/virtual-table';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { AdminFilterBar } from '@/components/admin/AdminFilterBar';
import { AdminSectionCard } from '@/components/admin/AdminSectionCard';
import { toAdminUserDetailPath } from '@/lib/routes/admin-route-catalog';
import { listUsers, type AdminUser } from '@/services/admin-api';
import { format } from 'date-fns';
import { toSignupSourceBucket } from '@atsready/shared';
import { signupSourceBadge } from '@/lib/signup-source';
import { cn } from '@/lib/utils';

function getTierVariant(tier: string): 'default' | 'info' | 'secondary' {
  switch (tier) {
    case 'PRO': return 'info'
    case 'FREE': return 'secondary'
    default: return 'default'
  }
}

function getStatusVariant(status: string): 'success' | 'info' | 'warning' | 'destructive' | 'outline' {
  switch (status) {
    case 'ACTIVE': return 'success'
    case 'TRIALING': return 'info'
    case 'PAST_DUE': return 'warning'
    case 'CANCELED': return 'destructive'
    default: return 'outline'
  }
}

export default function Users() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, pageSize, search],
    queryFn: () => listUsers({ page, pageSize, search: search || undefined }),
  });

  const users = data?.data || [];
  const pagination = data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  // Minimums and fr from the approved M14 mockup: header and rows share one
  // template, and the table scrolls sideways once it is narrower than 1196px.
  const userColumns: VirtualColumnDef[] = [
    { key: 'user', header: 'User', minWidth: 220, fr: 2.2, className: 'min-w-0 overflow-hidden' },
    { key: 'role', header: 'Role', minWidth: 148, fr: 0.8, className: 'min-w-0 overflow-hidden' },
    { key: 'tier', header: 'Tier', minWidth: 124, fr: 0.8, className: 'min-w-0 overflow-hidden' },
    { key: 'source', header: 'Source', minWidth: 120, fr: 0.9, className: 'min-w-0 overflow-hidden' },
    { key: 'status', header: 'Status', minWidth: 140, fr: 0.8, className: 'min-w-0 overflow-hidden' },
    { key: 'aiCalls', header: 'AI Calls', minWidth: 96, fr: 0.7, className: 'min-w-0 overflow-hidden justify-end text-right' },
    { key: 'tokens', header: 'Tokens', minWidth: 112, fr: 0.8, className: 'min-w-0 overflow-hidden justify-end text-right' },
    { key: 'cost', header: 'Cost', minWidth: 104, fr: 0.7, className: 'min-w-0 overflow-hidden justify-end text-right' },
    { key: 'joined', header: 'Joined', minWidth: 132, fr: 1, className: 'min-w-0 overflow-hidden' },
  ];

  const renderUserRow = (user: AdminUser) => {
    const name = user.name || 'No name';
    const aiCalls = user.totalAiCalls > 0 ? user.totalAiCalls.toLocaleString() : '\u2014';
    const tokens = user.totalTokens > 0 ? user.totalTokens.toLocaleString() : '\u2014';
    const cost = user.totalCost > 0 ? `$${user.totalCost.toFixed(4)}` : '\u2014';
    const joined = format(new Date(user.createdAt), 'MMM d, yyyy');
    const source = signupSourceBadge(toSignupSourceBucket(user.signupSource));
    return (
      <>
        <div role="cell" className="flex h-full min-w-0 items-center px-4">
          <div className="min-w-0">
            <div className="truncate font-medium" title={name}>{name}</div>
            <div className="truncate text-xs text-muted-foreground" title={user.email}>{user.email}</div>
          </div>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center px-4">
          <Badge variant="outline" className="block max-w-full truncate" title={user.role}>{user.role}</Badge>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center px-4">
          <Badge variant={getTierVariant(user.tier)} className="block max-w-full truncate" title={user.tier}>{user.tier}</Badge>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center px-4">
          <Badge
            variant={source.variant}
            className={cn('block max-w-full truncate', source.className)}
            title={source.label}
          >
            {source.label}
          </Badge>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center px-4">
          <Badge variant={getStatusVariant(user.status)} className="block max-w-full truncate" title={user.status}>{user.status}</Badge>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center justify-end px-4 tabular-nums text-muted-foreground">
          <span className="truncate" title={aiCalls}>{aiCalls}</span>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center justify-end px-4 tabular-nums text-muted-foreground">
          <span className="truncate" title={tokens}>{tokens}</span>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center justify-end px-4 tabular-nums text-muted-foreground">
          <span className="truncate" title={cost}>{cost}</span>
        </div>
        <div role="cell" className="flex h-full min-w-0 items-center px-4 text-muted-foreground">
          <span className="truncate" title={joined}>{joined}</span>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Users" description="Manage user accounts, subscription tiers, and AI consumption" />

      <AdminSectionCard
        title="All Users"
        description={`${pagination?.total.toLocaleString() ?? '0'} users total`}
      >
        {/* Search */}
        <AdminFilterBar onSubmit={handleSearch}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by email or name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10"
            />
          </div>
          <Button type="submit">Search</Button>
        </AdminFilterBar>

        {/* Table */}
        <div className="mt-4">
          <VirtualAdminTable
            data={users}
            columns={userColumns}
            renderRow={renderUserRow}
            onRowClick={(user) => navigate(toAdminUserDetailPath(user.id))}
            rowClassName="cursor-pointer"
            loading={isLoading}
            emptyMessage="No users found"
            estimateSize={() => 64}
          />
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </AdminSectionCard>
    </div>
  );
}
